// The in-page Copilot: a panel docked beside every page. One conversation
// follows the person from page to page; each message carries the page it
// was sent from (shown as a removable chip) and any @ references, so
// Copilot knows exactly what "this" is. Plans are approved in place, Open
// buttons take the person to the right page, and the full Copilot page is
// one click away (Expand). Esc or Ctrl/Cmd+J closes it.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Dropdown, Tooltip } from "antd";
import { PlusOutlined, HistoryOutlined, ExpandAltOutlined, CloseOutlined, AudioOutlined } from "@ant-design/icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toDisplayBrand } from "../../utils/displayBrand";
import styled from "styled-components";
import { InsightComposerShell, InsightStyledTextArea, InsightSendButton } from "../../pages/Projects/ProjectsStyle";
import { MessageRow, MsgCol, Bubble, Stamp, ErrorNote, Subtle } from "../../pages/Operator/OperatorStyle";
import { md } from "../../pages/Operator/markdown";
import PlanCard from "../../pages/Operator/PlanCard";
import TaskCard from "../../pages/Operator/TaskCard";
import CardSteps, { taskStep } from "../../pages/Operator/CardSteps";
import useCardPlaces from "../../pages/Operator/cardPlaces";
import WorkingLine from "../../pages/Operator/WorkingLine";
import { MemoryNotes } from "../../pages/Operator/MoreCards";
import { SmallButton, TextButton } from "../../pages/Operator/CardStyle";
import { formatWhen, formatFull } from "../../pages/Operator/time";
import { listOperatorChatsApi, copilotWhereApi, searchCopilotRefsApi } from "../../store/actions/operatorAction";
import { useCopilot, COPILOT_CHANGED } from "./CopilotContext";
import useCopilotChat from "./useCopilotChat";
import { useCopilotGo, NavigateActions, ChoiceActions, answerAfter } from "./navigation";
import { useAttachments, AttachButton, AttachmentChips, FileDropHost } from "../../pages/Operator/Attachments";
import {
  Panel, Head, HeadTitle, IconButton, Body, Empty, Chips, Where,
  Composer, ContextRow, ContextChip, RefMenu, Hint, HistoryList, SuggestRow, HighlightStyle, MicButton,
} from "./CopilotPanelStyle";

/** What the person can see on the page right now (its visible text, capped), so
 *  Copilot can read pages that have no check of their own. Never the panel. */
function screenText() {
  const main = document.querySelector("main.appPageContainer") || document.querySelector(".mainContent");
  if (!main) return "";
  return String(main.innerText || "").replace(/[ \t]+/g, " ").replace(/\n{3,}/g, "\n\n").trim().slice(0, 8000);
}

// The shared composer, with room for the dictation button too.
const PanelComposerShell = styled(InsightComposerShell)`
  padding-right: ${(p) => (p.$mic ? 100 : 64) + (p.$clip ? 36 : 0)}px;
`;

// A message sent while Copilot worked, by where it is.
const QUEUED_NOTE = {
  waiting: "Queued: Copilot reads it at its next step",
  read: "Read while working",
  left: "Sending next",
};

const TYPE_WORD = { study: "Study", project: "Library", agent: "Interview agent", survey: "Survey", task: "Fielding agent" };

/** Starter questions for the page the person is on. */
function starters(page) {
  const t = page && page.object && page.object.type;
  if (t === "study") return ["How is this study doing?", "Is anything wrong with this study?", "Build a library from this study"];
  if (t === "project") return ["How many interviews here are complete or incomplete?", "Which studies feed this library?", "Link another study to this library"];
  if (t === "agent") return ["Can this interviewer take calls?", "Which studies use this interviewer?", "Change a question for one study only"];
  if (t === "survey") return ["Review these questions against the design rules", "Build an interviewer from this survey"];
  if (page && page.page === "studies") return ["Which live studies are behind pace?", "Set up a new panel study"];
  if (page && page.page === "libraries") return ["Which library has the most incomplete interviews?", "Make a library from a study"];
  return ["What needs my attention today?", "Set up a new study", "Take me to my live studies"];
}

const CopilotPanel = ({ floating }) => {
  const { setOpen, chatId, setChatId } = useCopilot();
  const location = useLocation();
  // Open a page Copilot chose: hand over a form draft first, then point at
  // the control it named once the page has rendered it.
  const go = useCopilotGo();
  const [draft, setDraft] = useState("");
  const attach = useAttachments();
  const [page, setPage] = useState(null);
  const [usePage, setUsePage] = useState(true);
  // Where the person is when a left-over message goes out after a turn.
  const hereRef = useRef(null);
  const getContext = useCallback(() => hereRef.current, []);
  // Stopped with messages Copilot had not read yet: back into the box.
  const restoreDraft = useCallback((text) => setDraft((d) => (d.trim() ? `${text}\n\n${d}` : text)), []);
  const chat = useCopilotChat(chatId, { onChatCreated: setChatId, onNavigate: go, getContext, onRestoreDraft: restoreDraft });
  const [refs, setRefs] = useState([]);
  // "Something else" on a clarifying question: the box asks for the answer.
  const [askOther, setAskOther] = useState(false);
  const [refMenu, setRefMenu] = useState(null); // { query, items, index }
  const [history, setHistory] = useState([]);
  const inputRef = useRef(null);
  const endRef = useRef(null);
  const path = location.pathname;
  hereRef.current = usePage && page ? { path, page, screen: screenText() } : null;

  // What the panel is "looking at": resolved by the platform from the path,
  // and again after Copilot changes something (the chips follow the state).
  const [whereTick, setWhereTick] = useState(0);
  useEffect(() => {
    let alive = true;
    copilotWhereApi(path).then((d) => { if (alive) setPage(d.page || null); }).catch(() => { if (alive) setPage(null); });
    return () => { alive = false; };
  }, [path, whereTick]);
  useEffect(() => { setUsePage(true); }, [path]);
  useEffect(() => {
    const bump = () => setWhereTick((n) => n + 1);
    window.addEventListener(COPILOT_CHANGED, bump);
    return () => window.removeEventListener(COPILOT_CHANGED, bump);
  }, []);
  useEffect(() => { if (!chat.streaming) setWhereTick((n) => n + 1); }, [chat.streaming]);

  // Focus stays with the conversation: on open, and when an answer finishes
  // (the box is disabled while Copilot works, which drops focus).
  useEffect(() => { inputRef.current?.focus?.(); }, []);
  useEffect(() => { if (!chat.streaming) inputRef.current?.focus?.(); }, [chat.streaming]);

  const panelRef = useRef(null);
  const close = useCallback(() => {
    setOpen(false);
    // Back to where the person opened it from.
    setTimeout(() => document.getElementById("copilot-launcher")?.focus(), 0);
  }, [setOpen]);

  // Esc closes the panel when focus is in it or nowhere in particular, never
  // while a menu, dialog or another field on the page has focus.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      const el = document.activeElement;
      const inPanel = panelRef.current && panelRef.current.contains(el);
      if (inPanel || !el || el === document.body) close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [chat.messages.length, chat.interim.length, chat.liveAnswer, chat.streaming]);

  // @ references: the word after the last "@" before the caret.
  const refQuery = (text) => {
    const m = /(^|\s)@([^\s@]{0,40})$/.exec(text);
    return m ? m[2] : null;
  };
  const searchSeq = useRef(0);
  const onChange = (e) => {
    const text = e.target.value;
    setDraft(text);
    const q = refQuery(text);
    if (q === null) { setRefMenu(null); return; }
    const seq = ++searchSeq.current;
    setRefMenu((m) => ({ query: q, items: m ? m.items : [], index: 0, loading: true }));
    searchCopilotRefsApi(q).then((d) => {
      if (seq !== searchSeq.current) return;
      setRefMenu({ query: q, items: (d.items || []).slice(0, 12), index: 0, loading: false });
    }).catch(() => setRefMenu(null));
  };
  const pickRef = (item) => {
    if (!item) return;
    setRefs((r) => (r.some((x) => x.id === item.id) ? r : [...r, item]));
    setDraft((d) => d.replace(/(^|\s)@([^\s@]{0,40})$/, `$1${item.name} `));
    setRefMenu(null);
    inputRef.current?.focus?.();
  };

  // Dictation: the browser's own speech recognition, where it exists.
  const Speech = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const toggleDictation = () => {
    if (!Speech) return;
    if (listening) { recRef.current?.stop(); return; }
    const rec = new Speech();
    rec.lang = "en-US";
    rec.interimResults = true;
    rec.continuous = false;
    const base = draft ? `${draft.replace(/\s+$/, "")} ` : "";
    rec.onresult = (e) => {
      const text = Array.from(e.results).map((r) => r[0].transcript).join("");
      setDraft(base + text);
    };
    rec.onend = () => { setListening(false); inputRef.current?.focus?.(); };
    rec.onerror = () => setListening(false);
    recRef.current = rec;
    setListening(true);
    rec.start();
  };
  useEffect(() => () => recRef.current?.abort?.(), []);

  const submit = (text) => {
    const content = (text ?? draft).trim();
    // Files in the box go with a typed message, once they have uploaded.
    const files = text === undefined && !chat.streaming ? attach.sent : [];
    if (!content && !files.length) return;
    if (files.length && attach.uploading) return;
    const context = usePage && page ? { path, page, screen: screenText() } : null;
    // While Copilot works the message is queued; it reads it after its next step.
    if (chat.streaming) chat.queue(content, { context });
    else chat.send(content, { context, refs, attachments: files });
    if (files.length) attach.clear();
    setDraft("");
    setRefs([]);
    setRefMenu(null);
    setAskOther(false);
  };

  const onKeyDown = (e) => {
    if (refMenu && refMenu.items.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setRefMenu((m) => ({ ...m, index: (m.index + 1) % m.items.length })); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setRefMenu((m) => ({ ...m, index: (m.index - 1 + m.items.length) % m.items.length })); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); pickRef(refMenu.items[refMenu.index]); return; }
    }
    if (refMenu && e.key === "Escape") { e.preventDefault(); e.stopPropagation(); setRefMenu(null); return; }
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); }
  };

  // A plan run here changes what the page shows: tell the page to reload.
  const onPlanUpdated = (out) => {
    chat.onPlanUpdated(out);
    if (out && out.live) window.dispatchEvent(new CustomEvent(COPILOT_CHANGED, { detail: { plan: out.plan } }));
  };
  const onTaskUpdated = (task) => {
    chat.onTaskUpdated(task);
    window.dispatchEvent(new CustomEvent(COPILOT_CHANGED, { detail: { task } }));
  };
  // "Change it" / "Fix it with Copilot": the request goes into the box to edit.
  const revise = (text) => {
    setDraft(text);
    setTimeout(() => inputRef.current?.focus?.(), 0);
  };
  const currentContext = () => (usePage && page ? { path, page, screen: screenText() } : null);
  // A clarifying question answered by a tap: the option is the message, with what it points at.
  const pick = (o) => {
    if (chat.streaming) return;
    chat.send(o.label, { context: currentContext(), refs: o.ref && o.ref.id ? [{ type: o.ref.type, id: o.ref.id, name: o.label }] : [] });
    setAskOther(false);
  };
  const other = () => { setAskOther(true); setTimeout(() => inputRef.current?.focus?.(), 0); };
  // The page line under a message only when it differs from the one before.
  let lastWhere = null;
  const whereOf = (m) => {
    const w = m.context ? (m.context.object ? m.context.object.name : m.context.label) : null;
    const show = w && w !== lastWhere;
    if (w) lastWhere = w;
    return show ? w : null;
  };
  const shown = chat.messages.filter((m) => m.kind !== "simulation");
  const placeOf = useCardPlaces(chatId, shown, chat.plans, chat.tasks);
  const lastMsg = shown[shown.length - 1];
  // "Try again" on a failed answer sends the request before it once more.
  const askedBefore = (m) => {
    const i = shown.indexOf(m);
    for (let j = i - 1; j >= 0; j -= 1) if (shown[j].role === "user") return shown[j].content;
    return null;
  };
  const unanswered = !chat.streaming && lastMsg && lastMsg.role === "user" && !lastMsg.queued && !String(lastMsg._id).startsWith("local-");

  const [historyOpen, setHistoryOpen] = useState(false);
  const pickChat = (id) => { setHistoryOpen(false); setChatId(id); };
  const loadHistory = () => listOperatorChatsApi().then((d) => setHistory((Array.isArray(d) ? d : d.chats || []).slice(0, 25))).catch(() => setHistory([]));
  const historyMenu = (
    <HistoryList role="menu" aria-label="Recent conversations">
      {history.length ? history.map((c) => (
        <li key={c._id} role="menuitem" tabIndex={0} aria-current={c._id === chatId}
          onClick={() => pickChat(c._id)} onKeyDown={(e) => { if (e.key === "Enter") pickChat(c._id); }}>
          <span>{c.title || "New conversation"}</span>
          <small>{c.last_message_at ? formatWhen(c.last_message_at) : ""}</small>
        </li>
      )) : <li><small>No conversations yet.</small></li>}
    </HistoryList>
  );

  const pageLabel = page ? (page.object ? `${TYPE_WORD[page.object.type] || page.label}: ${page.object.name}` : page.label) : null;
  const empty = !chat.loading && !chat.messages.length && !chat.streaming;
  // The platform's chips for this page's real state; the static starters only
  // when the page has none.
  const suggestions = useMemo(() => (page && page.chips && page.chips.length
    ? page.chips
    : starters(page).map((l) => ({ label: l, prompt: l }))), [page]);

  return (
    <Panel ref={panelRef} role="complementary" aria-label="Copilot" $floating={floating}>
      <HighlightStyle />
      <FileDropHost attachments={attach} disabled={chat.streaming} />
      <Head>
        <HeadTitle title={chat.title || "Copilot"}>{chat.title || "Copilot"}</HeadTitle>
        <Tooltip title="New chat"><IconButton type="button" aria-label="New chat" disabled={chat.streaming} onClick={() => setChatId(null)}><PlusOutlined /></IconButton></Tooltip>
        <Dropdown dropdownRender={() => historyMenu} trigger={["click"]} open={historyOpen} onOpenChange={(o) => { setHistoryOpen(o); if (o) loadHistory(); }} placement="bottomRight" disabled={chat.streaming}>
          <span><Tooltip title="History" open={historyOpen ? false : undefined}><IconButton type="button" aria-label="Conversation history" disabled={chat.streaming}><HistoryOutlined /></IconButton></Tooltip></span>
        </Dropdown>
        <Tooltip title="Open in the full Copilot page"><IconButton type="button" aria-label="Expand" disabled={!chatId} onClick={() => { go(`/operator/${chatId}`); close(); }}><ExpandAltOutlined /></IconButton></Tooltip>
        <Tooltip title="Close (Esc)"><IconButton type="button" aria-label="Close Copilot" onClick={close}><CloseOutlined /></IconButton></Tooltip>
      </Head>

      <Body aria-live="polite">
        {empty ? (
          <Empty>
            <h3>{pageLabel ? "Ask about this page" : "How can I help?"}</h3>
            <p>{pageLabel ? `I can see ${pageLabel}. Type @ to point at any study, library, interviewer or survey.` : "Ask anything about your studies, interviewers and libraries. Type @ to point at one."}</p>
            <Chips>{suggestions.map((s) => <button key={s.label} type="button" onClick={() => submit(s.prompt)}>{s.label}</button>)}</Chips>
          </Empty>
        ) : null}

        {shown.map((m) => {
          const where = m.role === "user" ? whereOf(m) : null;
          return (
          <MessageRow key={m._id} id={`msg-${m._id}`} $user={m.role === "user"}>
            <MsgCol $user={m.role === "user"}>
              <Bubble $user={m.role === "user"}>
                {m.role === "user" ? (m.content || (m.attachments && m.attachments.length ? <Subtle>Attached files</Subtle> : null)) : (
                  <>
                    {m.content ? <ReactMarkdown remarkPlugins={[remarkGfm]} components={md}>{toDisplayBrand(m.content)}</ReactMarkdown> : null}
                    {placeOf(m).plan_ids.map((pid) => (chat.plans[pid] ? <PlanCard key={pid} plan={chat.plans[pid]} onUpdated={onPlanUpdated} onRevise={revise} compact /> : null))}
                    <CardSteps items={placeOf(m).task_ids.map((tid) => taskStep(chat.tasks[tid], <TaskCard key={tid} task={chat.tasks[tid]} onUpdated={onTaskUpdated} onAsk={(q) => submit(q)} />))} />
                    {placeOf(m).pointers.map((t) => <Subtle key={t} as="div" style={{ marginTop: 6 }}>{t}</Subtle>)}
                    {placeOf(m).moved ? (
                      <>
                        <Subtle as="div" style={{ marginTop: 12 }}>From earlier in this conversation</Subtle>
                        {placeOf(m).moved.plan_ids.map((pid) => (chat.plans[pid] ? <PlanCard key={pid} plan={chat.plans[pid]} onUpdated={onPlanUpdated} onRevise={revise} compact /> : null))}
                        {placeOf(m).moved.groups.map((g) => <CardSteps key={g.join()} items={g.map((tid) => taskStep(chat.tasks[tid], <TaskCard key={tid} task={chat.tasks[tid]} onUpdated={onTaskUpdated} onAsk={(q) => submit(q)} />))} />)}
                      </>
                    ) : null}
                    {placeOf(m).waiting ? (
                      <TextButton type="button" style={{ marginTop: 10 }} onClick={() => document.getElementById(`msg-${placeOf(m).waiting.at}`)?.scrollIntoView({ behavior: "smooth", block: "start" })}>
                        {placeOf(m).waiting.count} {placeOf(m).waiting.count === 1 ? "decision" : "decisions"} still waiting above
                      </TextButton>
                    ) : null}
                    <MemoryNotes memory={m.memory} />
                    <NavigateActions actions={m.actions} onGo={go} />
                    <ChoiceActions actions={m.actions} onPick={pick} onOther={other} answer={answerAfter(shown, m)} disabled={chat.streaming} content={m.content} />
                    {m.status === "aborted" ? <Subtle>Stopped.</Subtle> : null}
                    {m.error && m.status !== "aborted" ? (
                      <ErrorNote style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ flex: 1 }}>{m.error}</span>
                        {m === lastMsg && !chat.streaming && askedBefore(m) ? <SmallButton type="button" onClick={() => chat.send(askedBefore(m), { context: currentContext() })}>Try again</SmallButton> : null}
                      </ErrorNote>
                    ) : null}
                  </>
                )}
              </Bubble>
              {m.role === "user" ? <AttachmentChips files={m.attachments} style={{ marginTop: 6, marginBottom: 0, justifyContent: "flex-end" }} /> : null}
              {m.role === "user" && (where || (m.refs || []).length) ? (
                <Where>{[where && `on ${where}`, ...(m.refs || []).map((r) => `@${r.name}`)].filter(Boolean).join(" · ")}</Where>
              ) : null}
              {m.createdAt ? <Stamp title={formatFull(m.createdAt)}>{m.role === "user" ? "You" : "Copilot"} · {QUEUED_NOTE[m.queued] || formatWhen(m.createdAt)}</Stamp> : null}
            </MsgCol>
          </MessageRow>
          );
        })}
        {unanswered ? (
          <ErrorNote role="status" style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ flex: 1 }}>No answer came back for this message.</span>
            <SmallButton type="button" onClick={() => chat.retry(lastMsg._id, { context: currentContext() })}>Send again</SmallButton>
          </ErrorNote>
        ) : null}

        {chat.streaming ? (
          <MessageRow>
            <MsgCol>
              <Bubble>
                {chat.liveAnswer ? (
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={md}>{toDisplayBrand(chat.liveAnswer)}</ReactMarkdown>
                ) : (
                  <WorkingLine activity={chat.activity} interim={chat.interim} started={chat.startedAt} />
                )}
                {/* Cards from this turn wait until Copilot has finished explaining. */}
                {Object.values(chat.plans)
                  .filter((p) => p.status === "proposed" && !chat.messages.some((m) => (m.plan_ids || []).includes(p._id)))
                  .map((p) => <PlanCard key={p._id} plan={p} onUpdated={onPlanUpdated} compact held />)}
                <CardSteps items={chat.liveTaskIds.map((tid) => taskStep(chat.tasks[tid], <TaskCard key={tid} task={chat.tasks[tid]} onUpdated={onTaskUpdated} />))} />
                <NavigateActions actions={chat.liveActions} onGo={go} />
                <ChoiceActions actions={chat.liveActions} onPick={pick} onOther={other} disabled content={chat.liveAnswer} />
              </Bubble>
            </MsgCol>
          </MessageRow>
        ) : null}
        {chat.error && !unanswered && !(lastMsg && lastMsg.error) ? (
          <ErrorNote role="alert" style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ flex: 1 }}>{chat.error}</span>
            {lastMsg && lastMsg.role === "user" ? <SmallButton type="button" onClick={() => { chat.setError(null); chat.retry(lastMsg._id, { context: currentContext() }); }}>Try again</SmallButton> : null}
          </ErrorNote>
        ) : null}
        <div ref={endRef} />
      </Body>

      <Composer>
        {refMenu ? (
          <RefMenu role="listbox" aria-label="Reference a study, library, interviewer or survey">
            {refMenu.items.length ? refMenu.items.map((it, i) => (
              <li key={`${it.type}-${it.id}`} role="option" aria-selected={i === refMenu.index}
                onMouseDown={(e) => { e.preventDefault(); pickRef(it); }}>
                <span>{it.name}</span><small>{it.hint}</small>
              </li>
            )) : <li className="none">{refMenu.loading ? "Searching…" : "Nothing matches."}</li>}
          </RefMenu>
        ) : null}
        {!empty && !chat.streaming && !draft && suggestions.length ? (
          <SuggestRow aria-label="Suggestions for this page">
            {suggestions.slice(0, 3).map((s) => <button key={s.label} type="button" title={s.prompt} onClick={() => submit(s.prompt)}>{s.label}</button>)}
          </SuggestRow>
        ) : null}
        {(usePage && pageLabel) || refs.length ? (
          <ContextRow>
            {usePage && pageLabel ? (
              <ContextChip title="Copilot can see this page">
                <span className="t">{pageLabel}</span>
                <button type="button" aria-label="Do not use this page" onClick={() => setUsePage(false)}>×</button>
              </ContextChip>
            ) : null}
            {refs.map((r) => (
              <ContextChip key={r.id} $ref title={`${TYPE_WORD[r.type] || r.type}: ${r.name}`}>
                <span className="t">@{r.name}</span>
                <button type="button" aria-label={`Remove ${r.name}`} onClick={() => setRefs((l) => l.filter((x) => x.id !== r.id))}>×</button>
              </ContextChip>
            ))}
          </ContextRow>
        ) : null}
        <AttachmentChips files={attach.files} onRemove={attach.remove} />
        <PanelComposerShell $mic={!!Speech || chat.streaming} $clip>
          <InsightStyledTextArea
            ref={inputRef}
            placeholder={chat.streaming ? "Add to your request… Copilot reads it at its next step" : askOther ? "Type your answer" : "Ask anything… Type @ to reference"}
            value={draft}
            onChange={onChange}
            onKeyDown={onKeyDown}
            autoSize={{ minRows: 1, maxRows: 6 }}
            aria-label="Message Copilot"
          />
          {Speech && !chat.streaming ? (
            <MicButton type="button" onClick={toggleDictation} aria-pressed={listening} $on={listening}
              aria-label={listening ? "Stop dictation" : "Dictate a message"} title={listening ? "Listening… click to stop" : "Dictate"}>
              <AudioOutlined />
            </MicButton>
          ) : null}
          <AttachButton attachments={attach} right={(Speech && !chat.streaming) || chat.streaming ? 88 : 52} disabled={chat.streaming} />
          {chat.streaming ? (
            // Stop stays one click away; Send queues what the person adds.
            <MicButton type="button" onClick={chat.stop} aria-label="Stop" title="Stop">
              <svg width="14" height="14" viewBox="0 0 18 18" fill="none" aria-hidden="true"><rect x="3" y="3" width="12" height="12" fill="currentColor" rx="1" /></svg>
            </MicButton>
          ) : null}
          <InsightSendButton type="button" onClick={() => submit()} disabled={chat.streaming ? !draft.trim() : (!draft.trim() && !attach.sent.length) || attach.uploading} aria-label={chat.streaming ? "Add to the request" : "Send"}>
            <img src="/images/send-icon.svg" alt="" />
          </InsightSendButton>
        </PanelComposerShell>
        <Hint><span>Enter to send · Shift+Enter for a new line</span><span>Esc to close</span></Hint>
      </Composer>
    </Panel>
  );
};

export default CopilotPanel;
